import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../common/Header";
import Footer from "../common/Footer";
import { capitalizeFirstLetters } from "../../utils/stringUtils";
import { ReportStatusType } from "../../types";
import ErrorModal from "../modals/ErrorModal";
import { getApiUrl } from "../../utils/api";
import { isLoggedIn } from "../../utils/authHelpers";
import devLogger from "../../utils/devLogger";
import { FaCheck, FaCircle } from "react-icons/fa6";

const StatusSkeleton = ({ loading = false }) => {
  if (!loading) return null;

  return (
    <div className="relative">
      <div className="border-l-2 ml-7 border-gray-300 animate-pulse">
        {[...Array(3)].map((_, i) => (
          <div className="flex items-center mb-8 ml-[-13px]" key={i}>
            <div className="w-6 h-6 rounded-full bg-gray-300 mr-4" />
            <div className="h-4 bg-gray-300 w-1/3 rounded" />
          </div>
        ))}
      </div>
    </div>
  );
};

const StatusProgress = ({ status }: { status: ReportStatusType }) => {
  const statuses = ["awaiting review", "under review", "reviewed"];

  const statusIndex = statuses.indexOf(status || "");

  return (
    <div className="relative pl-6">
      {statuses.map((status, index) => (
        <div
          key={index}
          className={`border-l-2 ml-[-24px] pl-6 h-20 ${
            index <= statusIndex ? "border-gray-7" : "border-gray-2"
          }`}
        >
          <div
            className={`flex border-gray-7 justify-center items-center absolute -left-3 w-6 h-6 rounded-full p-1 border-2 ${
              index <= statusIndex ? "bg-gray-7 " : "bg-white"
            }`}
          >
            {index <= statusIndex ? (
              <FaCheck size={12} className="text-white" />
            ) : (
              <FaCircle size={10} />
            )}
          </div>

          <p
            className={`text-lg font-semibold ${
              index <= statusIndex ? "text-gray-7" : "text-gray-4"
            }`}
          >
            {capitalizeFirstLetters(status)}
          </p>
        </div>
      ))}
    </div>
  );
};

const TrackReport = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [status, setStatus] = useState<ReportStatusType>("awaiting review");

  const fetchReportStatus = async (id: string) => {
    if (!id) return;

    try {
      setLoading(true);
      const response = await fetch(getApiUrl(`/report/report-status/${id}`));
      if (!response.ok) {
        throw new Error("An error occurred while fetching the report");
      }
      const result = (await response.text()) as ReportStatusType;
      setStatus(result);
    } catch (error) {
      // throw new Error(error.message);
      setError("Error fetching report details, please try again.");
      devLogger.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!id) return;
    fetchReportStatus(id);
  }, [id]);

  return (
    <div className="container mx-auto max-w-screen-2xl h-dvh relative flex flex-col">
      <Header mode="light" />
      <div className="flex flex-col justify-start items-center flex-grow 2xl:shadow-md p-6 overflow-y-hidden">
        <div className="w-auto md:w-[640px]">
          <h1 className="text-2xl font-bold mb-2">Status Progress</h1>
          <h2 className="text-lg font-semibold mb-4">
            Case N<sup>o</sup>: <span className="text-lg ml-1">{id}</span>
          </h2>

          <StatusSkeleton loading={loading && !error} />

          {error && (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-md z-50">
              <ErrorModal
                errorDescription={error}
                onAccept={() => window.location.reload()}
                onClose={() => navigate("/")}
              />
            </div>
          )}

          {!loading && !error && status && (
            <div className="flex flex-col gap-[60px]">
              <div className="ml-7">
                <StatusProgress status={status} />
              </div>
              {!isLoggedIn() ? (
                <div className="flex flex-col items-stretch xs:flex-row xs:items-center gap-4 font-extrabold text-lg leading-[22px]">
                  <button
                    className="py-3 bg-gray-7 text-white rounded-[24px] px-[43.5px]"
                    onClick={() => navigate("/register")}
                  >
                    Create an Account
                  </button>
                  <button
                    className="py-3 bg-white border-2 border-gray-7 text-gray-7 rounded-[24px] px-[43.5px]"
                    onClick={() => navigate("/login")}
                  >
                    Sign In
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-stretch xs:flex-row xs:items-center gap-4 font-extrabold text-lg leading-[22px]">
                  <button
                    className="py-3 bg-gray-7 text-white rounded-[24px] px-[43.5px]"
                    onClick={() => navigate("/submit")}
                  >
                    Create a Report
                  </button>
                  <button
                    className="py-3 bg-white border-2 border-gray-7 text-gray-7 rounded-[24px] px-[43.5px]"
                    onClick={() => navigate("/dashboard")}
                  >
                    Go to Dashboard
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <Footer mode="dark" type="short" />
    </div>
  );
};

export default TrackReport;
